import {
  useGetRoomReservationSummary,
  type RoomReservationSummary,
} from '@exam-center/exam-location-management';
import { APP_PERMISSIONS } from '@exam-center/permission';
import {
  CancelOutlinedIcon,
  MoreMenuIconButton,
  type MoreMenuIconButtonOption,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { usePermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useCallback, useMemo, useState } from 'react';

import { type AdminExamCenterLocationUsageItem } from '../../components';
import { CannotRevokeExamCenterLocationPermissionModal } from '../cannot-revoke-exam-center-location-permission-modal';
import { RevokeExamCenterLocationPermissionModal } from '../revoke-exam-center-location-permission-modal';
import { RevokeLocationLoadingModal } from '../revoke-location-loading-modal';

interface ExamCenterLocationUsageActionMenuProps {
  row: AdminExamCenterLocationUsageItem;
}

interface ExamCenterLocationUsageActionMenuOption extends MoreMenuIconButtonOption {
  requiredPermission?: string[];
}

export default function ExamCenterLocationUsageActionMenu(
  props: Readonly<ExamCenterLocationUsageActionMenuProps>,
) {
  const { row } = props;
  const { permissions } = usePermission();
  const [isLoadingModalOpen, loadingModalHandlers] = useDisclosure(false);
  const [isRevokeModalOpen, revokeModalHandlers] = useDisclosure(false);
  const [isCannotRevokeModalOpen, cannotRevokeModalHandlers] = useDisclosure(false);
  const [reservation, setReservation] = useState<RoomReservationSummary | null>(null);

  const { checkReservations } = useGetRoomReservationSummary();
  const { showSnackbar } = useSnackbar();

  const handleRevokeClick = useCallback(async () => {
    loadingModalHandlers.open();
    try {
      const result = await checkReservations(row.roomId, [row.examCenterCode]);
      loadingModalHandlers.close();

      if (!result.hasReservations) {
        revokeModalHandlers.open();
      } else {
        const examCenterReservation = result.reservations.find(
          (res) => res.examCenterCode === row.examCenterCode,
        );
        setReservation(examCenterReservation ?? null);
        cannotRevokeModalHandlers.open();
      }
    } catch (error) {
      loadingModalHandlers.close();
      showSnackbar({
        message: 'ไม่สามารถตรวจสอบสิทธิ์การใช้งานได้ กรุณาลองใหม่อีกครั้ง',
        variant: 'error',
      });
      console.error(error);
    }
  }, [
    row.roomId,
    row.examCenterCode,
    checkReservations,
    loadingModalHandlers,
    revokeModalHandlers,
    cannotRevokeModalHandlers,
    showSnackbar,
  ]);

  const options = useMemo<ExamCenterLocationUsageActionMenuOption[]>(() => {
    const baseOptions: ExamCenterLocationUsageActionMenuOption[] = [
      {
        icon: <CancelOutlinedIcon sx={{ color: 'error.main' }} />,
        label: 'ยกเลิกสิทธิ์การใช้งาน',
        onClick: handleRevokeClick,
        sx: { color: 'error.main' },
        requiredPermission: [
          APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_USAGE.UPDATE,
        ],
      },
    ];

    return baseOptions.filter(
      (option) => option.requiredPermission?.every((p: string) => permissions.includes(p)) ?? true,
    );
  }, [permissions, handleRevokeClick]);

  return (
    <>
      <MoreMenuIconButton options={options} />
      <RevokeLocationLoadingModal open={isLoadingModalOpen} />
      {isRevokeModalOpen ? (
        <RevokeExamCenterLocationPermissionModal
          open={isRevokeModalOpen}
          onClose={revokeModalHandlers.close}
          examCenterLocationId={row.id}
        />
      ) : null}
      {isCannotRevokeModalOpen && reservation ? (
        <CannotRevokeExamCenterLocationPermissionModal
          open={isCannotRevokeModalOpen}
          onClose={cannotRevokeModalHandlers.close}
          reservation={reservation}
        />
      ) : null}
    </>
  );
}
